import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { catchError, EMPTY, Observable, switchMap } from 'rxjs';
import { SuccessPopupComponent } from '../../../libraries/popup/components/success-popup/success-popup.component';
import {
    ICreatePostCommentModel,
    ICreatePostModel,
    IOptimisticPost,
    IOptimisticPostComment,
    IPostAuthorModel,
    IPostModel,
} from '../../../models/post.model';
import { IUploadPhotoAPIResponse } from '../../../models/upload.model';
import { StorageService } from '../../storage/storage.service';
import { UploadService } from '../upload/upload.service';
import { PostService } from './post.service';

@Injectable({
    providedIn: 'root',
})
export class PostCreationService {
    constructor(
        private postService: PostService,
        private storageService: StorageService,
        private uploadService: UploadService,
        private dialog: MatDialog,
    ) {}

    createOptimistic(payload: ICreatePostModel, tempId: number): IOptimisticPost {
        return {
            id: -tempId,
            tempId: `temp-${tempId}`,
            isOptimistic: true,
            content: payload.content,
            type: payload.type,
            privacy: payload.privacy,
            createdAt: new Date().toISOString(),
            liked: false,
            likeCount: 0,
            replyCount: 0,
            sharedCount: 0,
            badgeCount: 0,
            saved: false,
            answered: false,
            blinking: false,
            author: {
                id: this.storageService.getLoginUser()?.id,
                name: this.storageService.getLoginUser()?.name,
                photo: this.storageService.getLoginUser()?.photo,
                verified: this.storageService.getLoginUser()?.verified,
            } as IPostAuthorModel,
            cats: payload.cats,
            verses: payload.selectedVerses,
            photo: payload.photoUrl,
        };
    }

    createPostInBackground(payload: ICreatePostModel): Observable<IPostModel> {
        if (payload.photoFile) {
            return this.uploadPostImageFile(payload.photoFile).pipe(
                switchMap((uploadRes) =>
                    this.postService.createPost({
                        ...payload,
                        photo: uploadRes.id,
                    }),
                ),
            );
        }

        return this.postService.createPost(payload);
    }

    createOptimisticComment(payload: ICreatePostCommentModel, tempId: number): IOptimisticPostComment {
        return {
            id: -tempId,
            tempId: `temp-${tempId}`,
            isOptimistic: true,
            content: payload.content,
            createdAt: new Date().toISOString(),
            liked: false,
            likeCount: 0,
            replyCount: 0,
            author: {
                id: this.storageService.getLoginUser()?.id,
                name: this.storageService.getLoginUser()?.name,
                photo: this.storageService.getLoginUser()?.photo,
                verified: this.storageService.getLoginUser()?.verified,
            } as IPostAuthorModel,
            verses: payload.selectedVerses,
            attachment: payload.photoUrl,
        };
    }

    private uploadPostImageFile(file: File): Observable<IUploadPhotoAPIResponse> {
        const formData = new FormData();
        formData.append('file', file);

        return this.uploadService.uploadPhoto(formData).pipe(
            catchError((error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error uploading photo', error);
                return EMPTY;
            }),
        );
    }
}
